// src/components/documents/ui/DocumentsPageUI.tsx
import { cn } from "@/lib/utils"
import type { ReactNode, HTMLAttributes } from "react"

export const documentsPageTheme = {
  mainContainer: "p-6 md:p-10 min-h-screen bg-gradient-to-br from-slate-950 via-purple-950/90 to-indigo-950/80",

  header: "flex flex-col sm:flex-row sm:items-center justify-between mb-8 gap-4",

  titleContainer: "text-6xl lg:text-7xl font-bold bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent",

  subtitle: "text-purple-300 mt-2 text-xl",

  newButton: "bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 shadow-lg shadow-purple-500/30 rounded-3xl text-xl px-10 py-7",

  tabsContainer: "flex bg-purple-950/60 backdrop-blur-2xl border border-purple-300/40 rounded-3xl p-1",

  tabButton: (active: boolean) => cn(
    "rounded-3xl px-10 py-4 text-lg font-medium transition-all",
    active 
      ? "bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-lg" 
      : "text-purple-300 hover:text-purple-100 hover:bg-purple-900/40"
  ),

  filterContainer: "flex items-center gap-3",

  filterLabel: "text-lg text-purple-300 font-medium whitespace-nowrap",

  selectTrigger: cn(
    "w-56 bg-purple-950/70 backdrop-blur-2xl border border-purple-300/40 text-white h-11 text-lg",
    "hover:border-purple-400 focus:border-purple-400 rounded-3xl px-6"
  ),

  selectContent: "bg-purple-950/90 backdrop-blur-2xl border border-purple-300/40 rounded-3xl shadow-2xl py-2 z-50",

  selectItem: "px-6 py-4 text-lg text-purple-200 hover:bg-purple-900/60 hover:text-white cursor-pointer rounded-2xl mx-1 transition-colors",

  searchContainer: "relative w-full sm:w-96",

  searchInput: "pl-12 bg-purple-950/70 border-purple-300/40 text-white placeholder:text-purple-400/70 rounded-3xl text-xl h-12",

  tableWrapper: "rounded-3xl overflow-hidden border border-purple-300/40 bg-purple-950/60 backdrop-blur-2xl shadow-2xl",

  tableHeader: "bg-purple-950/70",

  tableHeaderCell: "text-purple-200 text-3xl font-semibold py-6",

  tableRow: "hover:bg-purple-950/80 transition-colors cursor-pointer group",

  typeBadge: (color: string) => cn("text-white border-0 px-6 py-3 text-2xl rounded-2xl", color),

  statusBadge: (color: string) => cn("text-white border-0 px-6 py-3 text-2xl rounded-2xl", color),

  approvalBadge: cn(
    "ml-2 bg-gradient-to-r from-orange-500 to-amber-500 text-white text-base font-bold",
    "px-3.5 min-w-[28px] h-7 flex items-center justify-center rounded-2xl shadow-lg shadow-orange-500/60 ring-1 ring-orange-400/30 animate-pulse border border-orange-400/30 tracking-tight"
  ),

  unreadBadge: cn(
    "absolute top-1/2 -right-1 -translate-y-1/2 bg-gradient-to-r from-orange-500 to-amber-500 text-white",
    "text-base font-bold min-w-[34px] h-8 flex items-center justify-center rounded-2xl",
    "shadow-lg shadow-orange-500/60 ring-1 ring-orange-400/30 animate-pulse border border-orange-400/30 tracking-tight"
  ),

  historyCell: "text-purple-200 text-2xl",

  emptyCell: "h-48 text-center text-purple-300 text-2xl",

  modalForm: "max-w-4xl bg-purple-950/90 backdrop-blur-2xl border border-purple-300/40 text-white rounded-3xl",

  modalDetail: "max-w-6xl h-[90vh] p-0 bg-transparent border-0 overflow-hidden",

  modalEditor: "max-w-[95vw] h-[95vh] p-0 bg-transparent border-0 overflow-hidden",
} as const